import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { documentService } from "../../../services/documentService";
import { dropArt, dropHinweis, type DropArt } from "../detail/todoDrop";
import { leseAblageZug } from "../../mail/postfachAblage";
import { ausNeuemOutlook, legeNeuesOutlookAb } from "../../mail/neuesOutlook";
import { legeZugAbSichtbar } from "../../documents/ablage/zugAblage";
import { useAblagePlatzhalter } from "../../documents/ablage/useAblagePlatzhalter";
import { uploadFehlerText } from "../../documents/ablage/ablagePlatzhalter";
import { AblagePlatzhalterListe } from "../../documents/ablage/AblagePlatzhalterAnzeige";
import { nachMailDateiUpload } from "../../mail/mailDateiAblage";
import "./vorgangV4.css";
import { hinweisen } from "../../redesign/bestaetigen";
import { eigeneAblageUnter } from "../../notizbloecke/eigeneAblage";
import { alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/** Klasse am inneren Feld, das den Drop gerade selbst nimmt (Notiz, Kommentar). */
export const KLASSE_ABLAGE_ZIEL = "vg4-ablage-ziel";

type Props = {
    className?: string;
    vorgangId: number;
    todoId?: number;
    zielName: string;
    canEdit: boolean;
    onGeaendert: () => void | Promise<void>;
    children: React.ReactNode;
    "data-testid"?: string;
};

/**
 * Nimmt Dateien aus Finder/Explorer auf einer V4-Karte an — und seit
 * 01.11.2026 auch Mails und Mail-Anhänge aus dem Postfach (Mailliste,
 * Mailfenster), auch aus einem ausgelagerten Browserfenster heraus
 * (`mail/postfachAblage.ts`).
 *
 * Mails und Dokumente AUS dem Vorgang werden bewusst ignoriert: `dropArt`
 * priorisiert deren eigene MIME-Typen vor `Files`, sodass Mail- und
 * Dokumentverschiebungen nicht versehentlich einen zweiten Upload bzw. eine
 * zweite Verknüpfung auslösen.
 */
export const DateiDropZoneV4: React.FC<Props> = ({
    className,
    vorgangId,
    todoId,
    zielName,
    canEdit,
    onGeaendert,
    children,
    "data-testid": testId,
}) => {
    const dragTiefe = useRef(0);
    const ablage = useAblagePlatzhalter();
    const [aktiv, setAktiv] = useState(false);
    /**
     * Steht der Zeiger über einem Feld, das die Dateien selbst nimmt
     * (Notizfeld: Bilder, Kommentar: alles)? Dann zeigt die Karte KEIN Lila —
     * der Drop landet ja dort, nicht als Dokument an der Karte —, sondern
     * hebt das Feld hervor (User 22.10.2026).
     */
    const [imFeld, setImFeld] = useState(false);
    const feldRef = useRef<HTMLElement | null>(null);
    /** Was gerade über der Karte gezogen wird — für die Beschriftung. */
    const [zugArt, setZugArt] = useState<DropArt>(null);

    const markiereFeld = useCallback((el: HTMLElement | null) => {
        if (feldRef.current === el) return;
        feldRef.current?.classList.remove(KLASSE_ABLAGE_ZIEL);
        el?.classList.add(KLASSE_ABLAGE_ZIEL);
        feldRef.current = el;
        setImFeld(!!el);
    }, []);

    /** Zurück in die Ruhe — nach Drop, Abbruch, Esc oder Drop außerhalb. */
    const beenden = useCallback(() => {
        dragTiefe.current = 0;
        setAktiv(false);
        markiereFeld(null);
    }, [markiereFeld]);

    /*
     * Ein Ziehen kann enden, ohne dass diese Karte es erfährt: Drop auf ein
     * Kind, das ihn selbst nimmt und die Kette anhält, Drop außerhalb der
     * Karte, Esc, Verlassen des Fensters. Zählt `dragTiefe` dann nicht sauber
     * auf null, stand das Lila für immer über der Karte („jetzt wird nur das
     * Lila angezeigt", User 22.10.2026). Deshalb horcht die Karte, solange sie
     * leuchtet, am Fenster mit.
     */
    useEffect(() => {
        if (!aktiv) return;
        const ende = () => beenden();
        const fensterVerlassen = (e: DragEvent) => {
            // `relatedTarget === null` heißt: der Zeiger hat das Dokument verlassen.
            if (e.relatedTarget == null && (e.clientX <= 0 || e.clientY <= 0 ||
                e.clientX >= aktivesFenster().innerWidth || e.clientY >= aktivesFenster().innerHeight)) beenden();
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") beenden();
        };
        alleFenster.addEventListener("drop", ende, true);
        alleFenster.addEventListener("dragend", ende, true);
        alleFenster.addEventListener("dragleave", fensterVerlassen, true);
        alleFenster.addEventListener("keydown", taste, true);
        return () => {
            alleFenster.removeEventListener("drop", ende, true);
            alleFenster.removeEventListener("dragend", ende, true);
            alleFenster.removeEventListener("dragleave", fensterVerlassen, true);
            alleFenster.removeEventListener("keydown", taste, true);
        };
    }, [aktiv, beenden]);

    // Beim Abbau keine Hervorhebung an einem fremden Feld zurücklassen.
    useEffect(() => () => feldRef.current?.classList.remove(KLASSE_ABLAGE_ZIEL), []);

    /** Was die Karte aus diesem Zug machen kann — `null`: nichts. */
    const annehmbar = (transfer: DataTransfer): DropArt => {
        if (!canEdit) return null;
        const art = dropArt(Array.from(transfer.types ?? []));
        return art === "dateien" || art === "postfachMail" || art === "anhang" || art === "neuesOutlook" ? art : null;
    };
    // Ein Feld in der Karte (Notiz, Kommentar) nimmt nur echte Dateien selbst.
    const feldUnter = (event: React.DragEvent<HTMLElement>, art: DropArt) =>
        art === "dateien" ? eigeneAblageUnter(event.target, event.dataTransfer) : null;

    const dragEnter = (event: React.DragEvent<HTMLElement>) => {
        const art = annehmbar(event.dataTransfer);
        if (!art) return;
        event.preventDefault();
        dragTiefe.current += 1;
        setAktiv(true);
        setZugArt(art);
        markiereFeld(feldUnter(event, art));
    };

    const dragOver = (event: React.DragEvent<HTMLElement>) => {
        const art = annehmbar(event.dataTransfer);
        if (!art) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        if (!aktiv) setAktiv(true);
        if (zugArt !== art) setZugArt(art);
        markiereFeld(feldUnter(event, art));
    };

    const dragLeave = (event: React.DragEvent<HTMLElement>) => {
        if (!annehmbar(event.dataTransfer)) return;
        // Wohin geht der Zeiger? Bleibt er in der Karte, ist das nur der
        // Wechsel zwischen zwei Kindern. Ohne Ziel (`relatedTarget === null`)
        // hat das Ziehen die Karte verlassen ODER wurde abgebrochen (Esc,
        // Drop außerhalb des Fensters) — Chrome schickt dann genau EIN
        // `dragleave`, und der Zähler allein blieb über null stehen.
        const nach = event.relatedTarget as Node | null;
        if (!nach || !event.currentTarget.contains(nach)) {
            beenden();
            return;
        }
        dragTiefe.current = Math.max(0, dragTiefe.current - 1);
        if (dragTiefe.current === 0) beenden();
    };

    const drop = async (event: React.DragEvent<HTMLElement>) => {
        // Hat ein Kind (Notizfeld, Kommentar, Dokument-Auswahl) den Drop schon
        // übernommen, gehört er nicht mehr uns — sonst würde doppelt
        // hochgeladen (T-16). Das Lila muss trotzdem weg: früher kehrte die
        // Karte hier zurück, BEVOR sie sich zurücksetzte, und blieb lila.
        if (event.defaultPrevented) {
            beenden();
            return;
        }
        const art = annehmbar(event.dataTransfer);
        if (!art) return;
        event.preventDefault();
        beenden();
        if (art === "neuesOutlook") {
            // Neues Outlook (User 03.11.2026): synchron lesen, dann übersetzen
            // und als Postfach-Mail denselben Weg gehen.
            const outlook = ausNeuemOutlook(event.dataTransfer);
            if (!outlook) return;
            void legeNeuesOutlookAb(outlook, (zug) =>
                legeZugAbSichtbar(zug, { vorgangId, todoId: todoId ?? null }, ablage, neuLaden),
            );
            return;
        }
        if (art === "postfachMail" || art === "anhang") {
            // Synchron lesen — nach dem ersten `await` ist der Inhalt weg.
            const zug = leseAblageZug(event.dataTransfer);
            if (!zug) return;
            // Sofort sichtbar (User 01.11.2026): je Mail/Anhang ein Platzhalter
            // in der Karte, bis der echte Eintrag geladen ist; Fehler bleiben
            // rot mit „Erneut versuchen“ stehen.
            void legeZugAbSichtbar(zug, { vorgangId, todoId: todoId ?? null }, ablage, neuLaden);
            return;
        }

        const files = Array.from(event.dataTransfer.files ?? []);
        if (files.length === 0) return;
        void ladeDateienHoch(files);
    };

    /** Echte Einträge neu laden, dann die fertigen Platzhalter im selben Zug räumen. */
    const neuLaden = useCallback(async () => {
        try {
            await onGeaendert();
        } finally {
            ablage.raeumen();
        }
    }, [onGeaendert, ablage]);

    /** Eine Datei hochladen und ihren Platzhalter auf Stand halten. */
    const ladeEineHoch = async (file: File, id: string): Promise<boolean> => {
        try {
            const dok = await documentService.upload(todoId != null ? { file, todoId } : { file, vorgangId });
            // Mails aus Outlook (`.msg`/`.eml`): nach dem Upload ihre Anhänge
            // auspacken (User 01.11.2026). Fehler dabei kippen den Upload nicht.
            const ausgepackt = await nachMailDateiUpload(file, dok);
            if (ausgepackt?.fehler) {
                void hinweisen({ titel: "Anhänge nicht vollständig übernommen", text: ausgepackt.fehler, ton: "danger" });
            }
            ablage.fertig([id]);
            return true;
        } catch (err) {
            ablage.scheitern(id, uploadFehlerText(err), () => {
                ablage.laeuftWieder(id);
                void ladeEineHoch(file, id).then((ok) => (ok ? neuLaden() : undefined));
            });
            return false;
        }
    };

    const ladeDateienHoch = async (files: File[]) => {
        const ids = ablage.starte(files.map((f) => ({ name: f.name, mimeType: f.type || null })));
        const ergebnisse = await Promise.all(files.map((file, i) => ladeEineHoch(file, ids[i])));
        if (ergebnisse.some(Boolean)) await neuLaden();
    };

    const hinweis =
        zugArt === "postfachMail" || zugArt === "anhang" || zugArt === "neuesOutlook" ? dropHinweis(zugArt, zielName) : `Dateien an „${zielName}“ hochladen`;
    const sichtbar = aktiv && !imFeld;

    return (
        <section
            className={`${className ?? ""}${sichtbar ? " vg4-datei-drop-aktiv" : ""}`.trim()}
            data-testid={testId}
            onDragEnter={dragEnter}
            onDragOver={dragOver}
            onDragLeave={dragLeave}
            onDrop={(event) => void drop(event)}
        >
            {children}
            <AblagePlatzhalterListe
                eintraege={ablage.eintraege}
                onErneut={ablage.erneutVersuchen}
                kannErneut={ablage.kannErneut}
                onEntfernen={(id) => ablage.weg([id])}
            />
            {sichtbar && (
                <div className="vg4-datei-drop-hinweis" aria-live="polite">
                    {hinweis}
                </div>
            )}
        </section>
    );
};
